import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Compass, ArrowLeft, MailCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.toLowerCase().trim() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 to prevent enumeration; only network errors bubble up
      toast.error("No se pudo enviar el correo. Revisa tu conexión e intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] topo-bg px-6 py-12">
      <div className="w-full max-w-md bg-white border border-[#E2DCD0] p-10 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <Compass className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
          <span className="font-display text-2xl text-[#0A1128]">VoyageMarkt</span>
        </div>

        {!sent ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mb-3">Recuperar acceso</p>
            <h1 className="font-display text-3xl text-[#0A1128] leading-tight">¿Olvidaste tu contraseña?</h1>
            <p className="text-[#5C616A] mt-3 mb-8 text-sm">
              Escribe tu email y te enviaremos un enlace para restablecerla. El enlace caduca en 1 hora.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C616A]">Email</Label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-white border-[#E2DCD0] rounded-sm"
                  autoFocus
                />
              </div>
              <Button
                data-testid="forgot-submit-button"
                type="submit"
                disabled={busy}
                className="w-full bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm h-11"
              >
                {busy ? "Enviando..." : "Enviar enlace de recuperación"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent-state">
            <MailCheck className="w-10 h-10 text-[#2D6A4F] mb-4" strokeWidth={1.5} />
            <h1 className="font-display text-3xl text-[#0A1128] leading-tight">Revisa tu email.</h1>
            <p className="text-[#5C616A] mt-3 mb-2 text-sm">
              Si <strong>{email}</strong> está registrado, recibirás un enlace para restablecer tu contraseña.
            </p>
            <p className="text-[#8A8F98] text-xs mt-4">
              ¿No te ha llegado? Revisa la carpeta de spam o vuelve a intentarlo en unos minutos.
            </p>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[#E2DCD0]">
          <Link
            data-testid="back-to-login-link"
            to="/login"
            className="text-sm text-[#5C616A] hover:text-[#0A1128] inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Volver al login
          </Link>
        </div>
      </div>
    </div>
  );
}
